import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { User, Settings, Sparkles, Rocket, LifeBuoy, LogOut, ChevronDown, ShieldCheck } from "lucide-react";
import client, { endSession, fetchProfilePhotoUrl } from "../lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

/**
 * Top-right account menu. Present on every logged-in surface. Links to
 * profile, preferences, what is new, upcoming, and support, plus sign out.
 */
export default function AccountMenu() {
  const nav = useNavigate();
  const [name, setName] = useState("You");
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    client.get("/consent/status")
      .then((r) => {
        // KLA-056: /consent/status returns display_name at the top
        // level, not nested under person, so the old person.display_name
        // read always fell back to "You" for every signed-in student.
        const raw = (r?.data?.display_name || r?.data?.person?.display_name || "").trim();
        const n = raw.split(/\s+/)[0];
        setName(n || "You");
      })
      .catch(() => setName("You"));
    fetchProfilePhotoUrl().then(setPhotoUrl);
    return () => setPhotoUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
  }, []);

  const initial = (name || "Y").slice(0, 1).toUpperCase();
  const signOut = () => { endSession().finally(() => nav("/")); };

  const link = (to, icon, label, tid) => (
    <DropdownMenuItem asChild data-testid={tid}>
      <Link to={to} className="flex items-center gap-2.5 cursor-pointer">{icon}<span>{label}</span></Link>
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="k-btn k-btn-ghost !py-1.5 !px-2 flex items-center gap-2"
          data-testid="account-menu-trigger"
          aria-label="account menu"
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={name}
              className="w-6 h-6 rounded-full object-cover"
              data-testid="account-menu-avatar"
            />
          ) : (
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold"
              style={{ background: "hsl(var(--klareet-ink))", color: "white" }}
              data-testid="account-menu-avatar-fallback"
            >
              {initial}
            </span>
          )}
          <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">{name}</span>
          <ChevronDown size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-60 shadow-2xl border"
        style={{ background: "hsl(var(--card))", borderColor: "hsl(var(--klareet-line))" }}
        data-testid="account-menu"
      >
        <DropdownMenuLabel>Signed in as {name}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {link("/account", <User size={15} />, "My account", "menu-profile")}
        {link("/account/security", <ShieldCheck size={15} />, "Security", "menu-security")}
        {link("/account/preferences", <Settings size={15} />, "Preferences", "menu-preferences")}
        {link("/whats-new", <Sparkles size={15} />, "What's new", "menu-whats-new")}
        {link("/upcoming", <Rocket size={15} />, "Upcoming features", "menu-upcoming")}
        {link("/support", <LifeBuoy size={15} />, "Support", "menu-support")}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut} className="flex items-center gap-2.5 cursor-pointer" data-testid="menu-signout">
          <LogOut size={15} /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
